import { forwardRef } from 'react';
import { Link as RouterLink, LinkProps as RouterLinkProps } from 'react-router-dom';
import { experimental_extendTheme as extendTheme, ThemeCssVarOverrides } from '@mui/material/styles';
import { typography } from '@/constant/theme.js';
import { LinkProps } from '@mui/material';

const LinkBehavior = forwardRef<HTMLAnchorElement, Omit<RouterLinkProps, 'to'> & { href: RouterLinkProps['to'] }>(
    (props, ref) => {
        const { href, ...other } = props;
        // Map href (MUI) -> to (react-router)
        // return <RouterLink ref={ref} to={href} {...other} />;
        return <RouterLink ref={ref} to={href} {...other} />;
    },
);

export const options: ThemeCssVarOverrides = {
    cssVarPrefix: 'v',
    typography: {
        fontFamily: "'Noto Sans KR', sans-serif",
        ...typography,
    },
    shape: {
        borderRadius: 6,
    },
    // shadows: [...extendTheme({}).shadows.map((shadow, i) => (
    //     // i === 3 ? '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)' : shadow
    //     shadow
    // ))] as ThemeOptions["shadows"],
    colorSchemes: {
        light: {
            palette: {
                primary: {
                    main: '#ff6aac', // #09a8fa
                    contrastText: '#fff',
                    dark: '#ad536d',
                },
                sellerColor: {
                    main: '#ff6aac',
                    contrastText: '#fff',
                    dark: '#ad536d',
                },
                sellerColorInvert: {
                    main: '#6f6f6f',
                    contrastText: '#fff',
                    dark: '#ff6aac',
                },

                custom: {
                    main: '#22dcf5',
                    light: '#333333',
                    dark: '#444444',
                    contrastText: '#cc3300',
                },
                studioBtn: {
                    main: '#6f6f6f',
                    dark: '#09a8fa',
                    contrastText: '#fff',
                },

                black: {
                    main: '#333',
                    light: '#444',
                    dark: '#fff',
                    contrastText: '#ebebeb',
                },

                white: {
                    main: '#fff',
                    light: '#fff',
                    dark: '#ebebeb',
                    contrastText: '#333',
                },

                contrast: {
                    main: '#333',
                    light: '#444',
                    dark: '#000',
                    contrastText: '#ebebeb',
                },

                text: {
                    primary: '#3a3a3a',
                },

                orange: {
                    main: '#ff9800',
                    light: '#ffb74d',
                    dark: '#f57c00',
                    contrastText: '#fff',
                },
                'deeper-orange': {
                    main: '#FB6D48',
                    light: '#FF9A7A',
                    dark: '#E55A3B',
                    contrastText: '#fff',
                },
            },
        },

        dark: {
            palette: {
                contrast: {
                    main: '#e6e6e6',
                    light: '#ffffff',
                    dark: '#bcbbbb',
                    contrastText: '#2a2a2a',
                },
                primary: {
                    main: '#ff6aac', // #fa9dc6
                    contrastText: '#2a2a2a',
                },
            },
        },
    },

    components: {
        MuiCssBaseline: {
            styleOverrides: (themeParam: any) => {
                // console.log('themeParam', themeParam)

                return {
                    '.App': {
                        minHeight: '100vh',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                };
            },
        },
        MuiPaper: {
            defaultProps: {
                elevation: 3,
            },
        },
        MuiCardHeader: {
            defaultProps: {
                titleTypographyProps: {
                    variant: 'h5',
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    '&.h-full': {
                        display: 'flex',
                        flexDirection: 'column',

                        '& .MuiCardContent-root': {
                            flexGrow: 1,
                        },
                    },
                },
            },
        },
        MuiGrid: {
            defaultProps: {
                spacing: 3,
            },
        },
        MuiGrid2: {
            defaultProps: {
                spacing: 3,
            },
            styleOverrides: {
                root: {
                    '&.MuiGrid2-container': {
                        '--Grid-nested-rowSpacing': 0,
                    },
                },
            },
        },

        MuiOutlinedInput: {
            mt: 1,
        },

        MuiTextField: {
            defaultProps: {
                FormHelperTextProps: {
                    sx: {
                        // position: 'absolute',
                        // top: '0.1em',
                        bottom: '0.5em',
                        left: 0,
                        right: 0,
                    },
                },
            },
            styleOverrides: {
                root: {
                    marginTop: '0.5rem',
                    paddingBottom: '1.5rem',
                },
            },
        },

        MuiFormControl: {
            styleOverrides: {
                root: {
                    marginTop: '0.5rem',
                    paddingBottom: '1.5rem',
                },
            },
        },

        MuiIcon: {
            defaultProps: {
                // baseClassName: 'material-icons-two-tone',
                baseClassName: 'material-icons-outlined',
            },
        },

        MuiDataGrid: {
            defaultProps: {
                autoHeight: true,
                pageSize: 15,
                rowsPerPageOptions: [5, 10, 15, 25, 50, 100],
            },
            styleOverrides: {
                cell: {
                    '&:focus, &:focus-within': {
                        outline: 'none',
                    },
                },
                columnHeader: {
                    '&:focus, &:focus-within': {
                        outline: 'none',
                    },
                },
            },
        },
        MuiFab: {
            styleOverrides: {
                sizeExtra: {
                    width: 23,
                    height: 23,
                    minHeight: 23,
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                sizeExtra: {
                    width: 23,
                    height: 23,
                    minHeight: 23,
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    '&.MuiButton-sizeSmall': {
                        fontSize: 'calc(var(--v-theme-typography-button-size, 0.875rem) * 0.9)',
                    },
                    '&.MuiButton-sizeLarge': {
                        fontSize: 'calc(var(--v-theme-typography-button-size, 0.875rem) * 1.15)',
                        fontWeight: 'calc(var(--v-theme-typography-button-weight, 500))*0.9',
                    },
                },

                contained: {
                    '&.gradient': {
                        backgroundImage: 'var(--v-theme-gradient, linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%))',
                        color: 'var(--v-theme-gradient-contrastText, #fff)',
                    },
                },

                text: {
                    '&.gradient': {
                        backgroundImage: 'var(--v-theme-gradient, linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%))',
                        backgroundClip: 'text',
                        textFillColor: 'transparent',
                        webkitBackgroundClip: 'text',
                        webkitTextFillColor: 'transparent',
                    },
                },
            },
        },
        MuiLink: {
            defaultProps: {
                component: LinkBehavior,
            } as LinkProps,
        },
        MuiButtonBase: {
            defaultProps: {
                LinkComponent: LinkBehavior,
            },
        },
        // MuiListItemButton: {
        //     defaultProps: {
        //         LinkComponent: LinkBehavior,
        //     },
        // },
    },
};

const theme = extendTheme(options);

// 소도몰 전용 테마 옵션 (파란색 계열)
export const sodomallOptions: ThemeCssVarOverrides = {
    ...options,
    colorSchemes: {
        light: {
            palette: {
                primary: {
                    main: '#2196F3', // 파란색
                    contrastText: '#fff',
                    dark: '#1565C0',
                    light: '#64B5F6',
                },
                sellerColor: {
                    main: '#2196F3',
                    contrastText: '#fff',
                    dark: '#1565C0',
                },
                sellerColorInvert: {
                    main: '#6f6f6f',
                    contrastText: '#fff',
                    dark: '#2196F3',
                },

                custom: {
                    main: '#22dcf5',
                    light: '#333333',
                    dark: '#444444',
                    contrastText: '#cc3300',
                },
                studioBtn: {
                    main: '#6f6f6f',
                    dark: '#2196F3',
                    contrastText: '#fff',
                },

                black: {
                    main: '#333',
                    light: '#444',
                    dark: '#fff',
                    contrastText: '#ebebeb',
                },

                white: {
                    main: '#fff',
                    light: '#fff',
                    dark: '#ebebeb',
                    contrastText: '#333',
                },

                contrast: {
                    main: '#333',
                    light: '#444',
                    dark: '#000',
                    contrastText: '#ebebeb',
                },

                text: {
                    primary: '#3a3a3a',
                },

                orange: {
                    main: '#ff9800',
                    light: '#ffb74d',
                    dark: '#f57c00',
                    contrastText: '#fff',
                },
                'deeper-orange': {
                    main: '#2196F3', // 소도몰에서는 파란색으로 변경
                    light: '#64B5F6',
                    dark: '#1565C0',
                    contrastText: '#fff',
                },
            },
        },

        dark: {
            palette: {
                contrast: {
                    main: '#e6e6e6',
                    light: '#ffffff',
                    dark: '#bcbbbb',
                    contrastText: '#2a2a2a',
                },
                primary: {
                    main: '#64B5F6', // 다크모드 파란색
                    contrastText: '#2a2a2a',
                },
            },
        },
    },
};

export const sodomallTheme = extendTheme(sodomallOptions);

export default theme;
